import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { ChatDemo } from "@/components/ChatDemo";
import { Button } from "@/components/ui/button";
import {
  Zap, MessageCircle, Mic, CalendarCheck, BellRing, Users, FileBarChart, ArrowRight, Check,
  Smartphone, ShieldCheck, Sparkles, Bot,
} from "lucide-react";

const FEATURES = [
  { icon: MessageCircle, title: "Atendimento 24h no WhatsApp", desc: "A IA responde seus clientes em segundos, no tom da sua marca, mesmo de madrugada." },
  { icon: Mic, title: "Entende áudios", desc: "Mensagens de voz são transcritas e respondidas como se fosse um atendente humano." },
  { icon: CalendarCheck, title: "Agenda sozinha", desc: "Oferece só horários livres do seu expediente e marca, remarca ou cancela quando o cliente confirma." },
  { icon: BellRing, title: "Você é avisado na hora", desc: "Cada agendamento, cancelamento ou nova mensagem chega no seu celular, mesmo com o app fechado." },
  { icon: Users, title: "CRM automático", desc: "Todo contato vira um cliente com histórico de conversas, agendamentos e observações." },
  { icon: FileBarChart, title: "Relatório diário às 20h", desc: "Resumo do dia e agenda de amanhã direto no seu WhatsApp." },
];
const STEPS = [
  { n: "01", title: "Crie sua conta", desc: "Nome da empresa, segmento, expediente e serviços. Leva 1 minuto." },
  { n: "02", title: "Conecte o WhatsApp", desc: "Escaneie um QR Code com o WhatsApp da empresa. Sem chip novo, sem aparelho extra." },
  { n: "03", title: "Deixe a IA trabalhar", desc: "Ela atende, agenda e te avisa. Você assume qualquer conversa com um clique." },
];

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/public/landing").then(({ data }) => setStats(data)).catch(() => {}); }, []);

  const start = () => navigate(user ? "/dashboard" : "/login?modo=cadastro");
  const wa = `https://wa.me/${stats?.sales_whatsapp || "5519994045522"}?text=${encodeURIComponent("Olá! Quero saber mais sobre a FlowIA")}`;

  return (
    <div className="relative min-h-screen overflow-hidden bg-background text-slate-100" data-testid="landing-page">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-40 top-[-10%] h-[520px] w-[520px] rounded-full bg-cyan-500/15 blur-[140px]" />
        <div className="absolute right-[-10%] top-[30%] h-[460px] w-[460px] rounded-full bg-blue-600/15 blur-[140px]" />
      </div>

      <header className="relative z-10 mx-auto flex max-w-7xl items-center justify-between px-5 py-5">
        <div className="flex items-center gap-2.5"><div className="grid h-9 w-9 place-items-center rounded-xl bg-cyan-400 text-slate-950 cyan-glow"><Zap className="h-5 w-5" strokeWidth={2.6} /></div><span className="font-display text-xl font-extrabold text-white">Flow<span className="text-cyan-400">IA</span></span></div>
        <nav className="flex items-center gap-2 sm:gap-4">
          <Link to="/planos" className="hidden text-sm text-slate-400 hover:text-cyan-300 sm:block" data-testid="landing-nav-plans">Planos</Link>
          {user ? <Button onClick={() => navigate("/dashboard")} className="bg-cyan-400 font-semibold text-slate-950 hover:bg-cyan-300" data-testid="landing-nav-dashboard">Ir para o painel</Button>
            : <><Link to="/login" className="text-sm text-slate-300 hover:text-white" data-testid="landing-nav-login">Entrar</Link><Button onClick={start} className="bg-cyan-400 font-semibold text-slate-950 hover:bg-cyan-300" data-testid="landing-nav-cta">Testar grátis</Button></>}
        </nav>
      </header>

      <section className="relative z-10 mx-auto grid max-w-7xl items-center gap-12 px-5 pb-20 pt-10 lg:grid-cols-[1.1fr_1fr] lg:pt-16">
        <div>
          <p className="mb-4 inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/5 px-3 py-1 text-xs font-mono-flow uppercase tracking-widest text-cyan-300"><Sparkles className="h-3.5 w-3.5" /> Assistente IA para WhatsApp</p>
          <h1 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">Sua empresa atendendo e <span className="text-cyan-400">agendando sozinha</span> no WhatsApp.</h1>
          <p className="mt-6 max-w-xl text-base text-slate-400 sm:text-lg">A FlowIA responde seus clientes com IA humanizada, entende áudios, marca horários na agenda e te avisa no celular. Você só aparece para atender.</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button onClick={start} className="h-12 bg-cyan-400 px-6 text-base font-semibold text-slate-950 hover:bg-cyan-300 cyan-glow" data-testid="landing-hero-cta">{user ? "Ir para o painel" : `Testar grátis por ${stats?.trial_days ?? 7} dias`} <ArrowRight className="ml-2 h-4 w-4" /></Button>
            <a href={wa} target="_blank" rel="noopener noreferrer"><Button variant="outline" className="h-12 border-slate-700 bg-slate-900/50 px-6 text-base text-slate-200 hover:bg-slate-800" data-testid="landing-hero-sales"><Smartphone className="mr-2 h-4 w-4" /> Falar com vendas</Button></a>
          </div>
          <p className="mt-3 text-xs text-slate-500">Sem cartão de crédito. Cancele quando quiser.</p>
          <div className="mt-10 grid max-w-md grid-cols-3 gap-4" data-testid="landing-stats">
            <Stat value="24h" label="atendimento sem pausa" />
            <Stat value="< 5s" label="tempo de resposta" />
            <Stat value={`${stats?.trial_days ?? 7} dias`} label="grátis para testar" />
          </div>
        </div>
        <div className="flex justify-center lg:justify-end"><ChatDemo /></div>
      </section>

      <section className="relative z-10 border-y border-slate-800/60 bg-slate-950/40 py-20">
        <div className="mx-auto max-w-7xl px-5">
          <p className="text-xs font-mono-flow uppercase tracking-widest text-cyan-400">Tudo no plano Starter</p>
          <h2 className="mt-2 max-w-2xl font-display text-base font-bold text-white md:text-lg">Um assistente completo que trabalha enquanto você atende, dirige ou descansa.</h2>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f, i) => (
              <div key={f.title} className="glow-card animate-flow-in rounded-2xl p-6" style={{ animationDelay: `${i * 80}ms` }} data-testid="landing-feature">
                <div className="grid h-11 w-11 place-items-center rounded-xl bg-cyan-400/10 text-cyan-300"><f.icon className="h-5 w-5" /></div>
                <h3 className="mt-4 font-display text-lg font-semibold text-white">{f.title}</h3>
                <p className="mt-1.5 text-sm text-slate-400">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative z-10 mx-auto max-w-7xl px-5 py-20">
        <div className="grid gap-10 lg:grid-cols-[1fr_1.2fr]">
          <div>
            <p className="text-xs font-mono-flow uppercase tracking-widest text-cyan-400">Como funciona</p>
            <h2 className="mt-2 font-display text-base font-bold text-white md:text-lg">Pronto em 3 passos, sem técnico.</h2>
            <div className="mt-8 space-y-6">
              {STEPS.map((s) => (
                <div key={s.n} className="flex gap-4" data-testid="landing-step">
                  <span className="font-mono-flow text-2xl font-bold text-cyan-400/70">{s.n}</span>
                  <div><p className="font-semibold text-white">{s.title}</p><p className="text-sm text-slate-400">{s.desc}</p></div>
                </div>
              ))}
            </div>
          </div>
          <div className="glow-card relative overflow-hidden rounded-3xl border-cyan-400/40 p-8 cyan-glow" data-testid="landing-pricing">
            <span className="absolute right-6 top-6 rounded-full bg-cyan-400 px-3 py-0.5 text-[11px] font-bold text-slate-950">7 DIAS GRÁTIS</span>
            <div className="flex items-center gap-2 text-cyan-300"><Bot className="h-5 w-5" /><p className="font-display text-xl font-bold text-white">Starter</p></div>
            <p className="mt-4"><span className="font-display text-5xl font-extrabold text-white">R$ {Math.round(stats?.price ?? 397)}</span><span className="text-slate-400">/mês</span></p>
            <p className="mt-1 text-sm text-slate-400">Pix ou cartão · ativação imediata · sem fidelidade</p>
            <ul className="mt-6 space-y-2.5 text-sm text-slate-200">
              {["WhatsApp com IA humanizada", "Transcrição de áudios", "Agenda inteligente + lembretes", "Notificações no celular", "CRM completo", "Relatório diário"].map((t) => <li key={t} className="flex items-center gap-2"><Check className="h-4 w-4 text-cyan-400" /> {t}</li>)}
            </ul>
            <Button onClick={start} className="mt-8 h-12 w-full bg-cyan-400 text-base font-semibold text-slate-950 hover:bg-cyan-300" data-testid="landing-pricing-cta">{user ? "Ir para o painel" : "Começar teste grátis"}</Button>
            <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-slate-500"><ShieldCheck className="h-3.5 w-3.5" /> Seus dados e os dos seus clientes ficam isolados por empresa.</p>
          </div>
        </div>
      </section>

      <footer className="relative z-10 border-t border-slate-800/60 py-8">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-5 text-xs text-slate-500">
          <span>© {new Date().getFullYear()} FlowIA · Assistente IA Empresarial</span>
          <div className="flex gap-4"><Link to="/planos" className="hover:text-cyan-300">Planos</Link><Link to="/login" className="hover:text-cyan-300">Entrar</Link><a href={wa} target="_blank" rel="noopener noreferrer" className="hover:text-cyan-300">Contato</a></div>
        </div>
      </footer>
    </div>
  );
}

function Stat({ value, label }) {
  return <div><p className="font-display text-2xl font-extrabold text-white">{value}</p><p className="text-xs text-slate-500">{label}</p></div>;
}
